import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CREDIT_STATUS_MAP, type CreditEnforcement, type CreditStatus } from '@goyone/shared';
import { api } from '../lib/api';
import { money, num } from '../lib/format';
import { Badge, Modal } from './ui';

export interface CreditCheck {
  status: CreditStatus;
  statusLabel: string;
  effectiveLimit: number | null;
  outstanding: number;
  available: number | null;
  usedPercent: number | null;
  amount: number;
  projected: number;
  exceeds: boolean;
  excess: number;
  blocked: boolean;
  message: string | null;
  enforcement: CreditEnforcement;
}

/**
 * حالة ائتمان عميل.
 *
 * تُسأل من الخادم لا تُحسب في المتصفح: الحساب هنا نسخة ثانية من القاعدة
 * تفترق عن الأولى عند أول تعديل، فيرى المستخدم رقمًا ويمنعه النظام بآخر.
 */
export function useCredit(accountId: string | null | undefined, amount = 0) {
  return useQuery({
    queryKey: ['credit', accountId, amount],
    queryFn: () =>
      api<CreditCheck>(
        `/finance/credit/check?accountId=${accountId}&amount=${amount}`,
      ),
    enabled: !!accountId,
  });
}

/** شريط مختصر لحالة العميل — للملف الشخصي ورؤوس المستندات. */
export function CreditBar({ credit }: { credit: CreditCheck }) {
  const def = CREDIT_STATUS_MAP[credit.status];

  return (
    <div className="credit-bar">
      <div className="credit-head">
        <Badge kind={def.kind}>{def.label}</Badge>
        <span className="small mute">{def.description}</span>
      </div>

      <div className="credit-figures">
        <span>
          المستحق <strong className="num">{money(credit.outstanding)}</strong>
        </span>
        <span>
          السقف{' '}
          <strong className="num">
            {credit.effectiveLimit == null ? 'بلا حد' : money(credit.effectiveLimit)}
          </strong>
        </span>
        <span>
          المتاح{' '}
          <strong className={`num${(credit.available ?? 0) < 0 ? ' neg' : ''}`}>
            {credit.available == null ? 'بلا حد' : money(credit.available)}
          </strong>
        </span>
      </div>

      {credit.usedPercent != null && credit.effectiveLimit ? (
        <div className="credit-track">
          <span
            className={`credit-fill credit-${credit.status.toLowerCase()}`}
            style={{ width: `${Math.min(100, credit.usedPercent)}%` }}
          />
        </div>
      ) : null}

      {credit.usedPercent != null && (
        <div className="tiny mute">استُهلك {num(credit.usedPercent)} بالمئة من السقف.</div>
      )}
    </div>
  );
}

/**
 * تنبيه يسبق إجراءً.
 *
 * يُعرض عند التجاوز وعند الاقتراب، ويصمت فيما عدا ذلك: تنبيهٌ يظهر دائمًا
 * يصير خلفيةً لا يقرؤها أحد.
 */
export function CreditNotice({ credit }: { credit: CreditCheck | undefined }) {
  if (!credit?.message) return null;

  return (
    <div className={`alert ${credit.exceeds ? 'alert-danger' : 'alert-warning'} mb-4`}>
      {credit.message}
      {credit.exceeds && credit.enforcement === 'WARN' && (
        <> يمكن المضيّ، والمسؤولية على من يقرّر.</>
      )}
    </div>
  );
}

/**
 * نافذة التخويل بالتجاوز.
 *
 * السبب مطلوب: تخويل بلا سبب لا يجيب عن السؤال الذي يُسأل في المراجعة،
 * وهو لماذا بيع لهذا العميل فوق سقفه.
 */
export function CreditOverrideDialog({
  credit,
  pending,
  onClose,
  onConfirm,
}: {
  credit: CreditCheck;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      title="تخويل بتجاوز سقف الائتمان"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => onConfirm(reason.trim())}
            disabled={reason.trim().length < 5 || pending}
          >
            {pending ? 'جارٍ التنفيذ' : 'أخوّل وأتحمل المسؤولية'}
          </button>
        </>
      }
    >
      <div className="alert alert-danger mb-4">{credit.message}</div>

      <div className="credit-figures mb-4">
        <span>
          المستحق حاليًا <strong className="num">{money(credit.outstanding)}</strong>
        </span>
        <span>
          بعد هذا المستند <strong className="num neg">{money(credit.projected)}</strong>
        </span>
        <span>
          الزائد عن السقف <strong className="num neg">{money(credit.excess)}</strong>
        </span>
      </div>

      <div className="field">
        <label className="label req" htmlFor="ovr-reason">
          سبب التخويل
        </label>
        <textarea
          id="ovr-reason"
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: قدّم العميل شيكًا مصدَّقًا بقيمة المستحق."
        />
        <span className="tiny mute">
          يُسجَّل باسمك وتاريخه في سجل التخويلات، ويظهر في المراجعة المالية.
        </span>
      </div>
    </Modal>
  );
}
